import {
  BRAND_REFRESH_BANNER_COUNTDOWN_DAYS,
  BRAND_REFRESH_DEBUG_COUNTDOWN_DAYS_STORAGE_KEY,
  BRAND_REFRESH_DEBUG_FORCE_EXPIRED_STORAGE_KEY,
  BRAND_REFRESH_DEBUG_STARTED_AT_STORAGE_KEY,
} from './constants'
import {
  hasDebugQueryParam,
  isDebugToolsActive,
  isDebugToolsEnv,
} from '../debug_tools/debugQuery'

export interface BrandRefreshDebugOverrides {
  startedAt: string | null
  countdownDays: number
  forceExpired: boolean
}

/** @deprecated Prefer `isDebugToolsEnv` from `debug_tools`. */
export function isBrandRefreshDebugEnv(): boolean {
  return isDebugToolsEnv()
}

/** @deprecated Prefer `hasDebugQueryParam` from `debug_tools` / `shared/constants/debug_query`. */
export function hasBrandRefreshBetaDebugQueryParam(search = ''): boolean {
  return hasDebugQueryParam(search)
}

/** True when brand-refresh debug overrides may be read or written. */
export function isBrandRefreshDebugActive(
  search = typeof window !== 'undefined' ? window.location.search : '',
): boolean {
  return isDebugToolsActive(search)
}

/** @param _cacheRevision Pass a changing value (e.g. from debug event listeners) to re-read localStorage. */
export function readBrandRefreshDebugOverrides(_cacheRevision = 0): BrandRefreshDebugOverrides {
  if (!isBrandRefreshDebugActive() || typeof localStorage === 'undefined') {
    return {
      startedAt: null,
      countdownDays: BRAND_REFRESH_BANNER_COUNTDOWN_DAYS,
      forceExpired: false,
    }
  }

  const startedAt = localStorage.getItem(BRAND_REFRESH_DEBUG_STARTED_AT_STORAGE_KEY)
  const countdownDaysRaw = localStorage.getItem(BRAND_REFRESH_DEBUG_COUNTDOWN_DAYS_STORAGE_KEY)
  const parsedDays = countdownDaysRaw == null ? NaN : Number(countdownDaysRaw)

  return {
    startedAt: startedAt || null,
    countdownDays: Number.isFinite(parsedDays) && parsedDays > 0
      ? parsedDays
      : BRAND_REFRESH_BANNER_COUNTDOWN_DAYS,
    forceExpired: localStorage.getItem(BRAND_REFRESH_DEBUG_FORCE_EXPIRED_STORAGE_KEY) === 'true',
  }
}

export const BRAND_REFRESH_DEBUG_UPDATED_EVENT = 'brand-refresh-beta-debug-updated'

export function notifyBrandRefreshDebugUpdated(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(BRAND_REFRESH_DEBUG_UPDATED_EVENT))
}

function setBrandRefreshDebugStartedAt(isoTimestamp: string): void {
  if (!isBrandRefreshDebugActive()) return
  localStorage.setItem(BRAND_REFRESH_DEBUG_STARTED_AT_STORAGE_KEY, isoTimestamp)
  localStorage.removeItem(BRAND_REFRESH_DEBUG_FORCE_EXPIRED_STORAGE_KEY)
  notifyBrandRefreshDebugUpdated()
}

export function setBrandRefreshDebugForceExpired(forceExpired: boolean): void {
  if (!isBrandRefreshDebugActive()) return
  if (forceExpired) {
    localStorage.setItem(BRAND_REFRESH_DEBUG_FORCE_EXPIRED_STORAGE_KEY, 'true')
  } else {
    localStorage.removeItem(BRAND_REFRESH_DEBUG_FORCE_EXPIRED_STORAGE_KEY)
  }
  notifyBrandRefreshDebugUpdated()
}

export function clearBrandRefreshDebugOverrides(): void {
  if (!isBrandRefreshDebugActive()) return
  localStorage.removeItem(BRAND_REFRESH_DEBUG_STARTED_AT_STORAGE_KEY)
  localStorage.removeItem(BRAND_REFRESH_DEBUG_COUNTDOWN_DAYS_STORAGE_KEY)
  localStorage.removeItem(BRAND_REFRESH_DEBUG_FORCE_EXPIRED_STORAGE_KEY)
  notifyBrandRefreshDebugUpdated()
}

/** Sets debug started-at to N days ago (simulates elapsed countdown time). */
export function setBrandRefreshDebugStartedDaysAgo(daysAgo: number): void {
  const startedMs = Date.now() - daysAgo * 24 * 60 * 60 * 1000
  setBrandRefreshDebugStartedAt(new Date(startedMs).toISOString())
}

/** Overrides the banner countdown length (days) for debug sessions. */
export function setBrandRefreshDebugCountdownDays(countdownDays: number): void {
  if (!isBrandRefreshDebugActive()) return
  if (!Number.isFinite(countdownDays) || countdownDays <= 0) return

  localStorage.setItem(
    BRAND_REFRESH_DEBUG_COUNTDOWN_DAYS_STORAGE_KEY,
    String(Math.floor(countdownDays)),
  )
  notifyBrandRefreshDebugUpdated()
}
